import React, { useMemo } from 'react';
import type { ImageStyle, ViewStyle } from 'react-native';
import { View, Image, Text, StyleSheet, Platform } from 'react-native';
import FadeIn from 'react-native-fade-in-image';
import Loader from 'src/presentation/components/atoms/Loader';
import { useContentWidth } from 'src/presentation/hooks/useContentWidth';
import Dimens from 'src/core/utils/Dimens';
import Fonts from 'src/core/utils/Fonts';
import Colors from 'src/core/utils/Colors';
import toOpaqueColor from 'src/core/utils/toOpaqueColor';

interface EventLinkProps {
	image: string;
	isLoading?: boolean;
	title: string;
	uuid?: string;
	theme?: Record<string, string>;
}

function EventLink({ image, isLoading, title, theme }: EventLinkProps) {
	const contentWidth = useContentWidth();
	const hasImage = !!image;
	// Tiles without an image get the event colour at full opacity instead of a faded (never-loaded) FadeIn
	// placeholder.
	const eventColor = toOpaqueColor(theme?.header_bg_color) ?? Colors.greyBg;

	const sizedStyles = useMemo((): {
		imgIos: ImageStyle;
		imgholder: ViewStyle;
		imgAndroid: ImageStyle;
		loadingOverlayIos: ViewStyle;
	} => {
		const imageHeightIos = (contentWidth - 60) / 2;
		const imageHeightAndroid = (contentWidth - 48) / 2;

		return {
			imgIos: {
				width: '100%',
				height: imageHeightIos,
				backgroundColor: Colors.greyBg,
				alignSelf: 'center',
				marginBottom: Dimens.margin5,
			},
			imgholder: {
				width: '100%',
				height: imageHeightAndroid,
				backgroundColor: Colors.greyBg,
				alignSelf: 'center',
			},
			imgAndroid: {
				width: '100%',
				height: imageHeightAndroid,
				backgroundColor: Colors.greyBg,
				alignSelf: 'center',
			},
			loadingOverlayIos: {
				// Cover only the image area (not the title below), so the spinner
				// sits in the middle of the image instead of lower, over image + title.
				position: 'absolute',
				top: 0,
				left: 0,
				right: 0,
				height: imageHeightIos,
				backgroundColor: 'rgba(255,255,255,0.6)',
				justifyContent: 'center',
				alignItems: 'center',
			},
		};
	}, [contentWidth]);

	// iOS: simple FadeIn image with the title below it.
	if (Platform.OS === 'ios') {
		return (
			<View>
				{hasImage ? (
					<FadeIn>
						<Image source={{ uri: image }} style={sizedStyles.imgIos} />
					</FadeIn>
				) : (
					<View style={[sizedStyles.imgIos, { backgroundColor: eventColor }]} />
				)}
				{isLoading && (
					<View style={sizedStyles.loadingOverlayIos}>
						<Loader bgColor={Colors.transparent} small={true} />
					</View>
				)}
				<Text style={styles.titleIos}>{title}</Text>
			</View>
		);
	}

	// Android: simple image with the title below it.
	return (
		<View>
			<View style={sizedStyles.imgholder}>
				{hasImage ? (
					<Image source={{ uri: image }} style={sizedStyles.imgAndroid} />
				) : (
					<View style={[sizedStyles.imgAndroid, { backgroundColor: eventColor }]} />
				)}
				{isLoading && (
					<View style={styles.loadingOverlayAndroid}>
						<Loader bgColor={Colors.transparent} small={true} />
					</View>
				)}
			</View>
			<View>
				<Text style={styles.titleAndroid}>{title}</Text>
			</View>
		</View>
	);
}

export default EventLink;

const styles = StyleSheet.create({
	titleIos: {
		textAlign: 'center',
		fontFamily: Fonts.montserratRegular,
		fontSize: Dimens.fontSize17,
		marginBottom: Dimens.margin15,
		height: 47,
	},
	titleAndroid: {
		textAlign: 'center',
		fontFamily: Fonts.montserratRegular,
		fontSize: Dimens.fontSize17,
		marginBottom: Dimens.margin15,
		marginTop: Dimens.margin5,
	},
	loadingOverlayAndroid: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: 'rgba(255,255,255,0.6)',
		justifyContent: 'center',
		alignItems: 'center',
	},
});
